import { AnalyticsEvent, AnalyticsDestination, SystemTypedEvents } from './types';
import { logger } from '@shared/logging/logger';

class AnalyticsCore {
  private destinations: AnalyticsDestination[] = [];
  private eventQueue: AnalyticsEvent[] = [];
  private offlineQueue: AnalyticsEvent[] = [];
  private anonymousId: string = '';
  private currentUserId: string | null = null;
  private isOffline = false;
  private batchSize = 10;
  private flushIntervalMs = 8000;
  private timerId: any = null;
  private isReportingAnonymously = false;

  constructor() {
    this.initializeDeviceState();
  }

  /**
   * Safe setup of persistent cookie/localStorage identifiers for retention tracking.
   */
  private initializeDeviceState() {
    if (typeof window === 'undefined') return;

    // Load or create a persistent UUID representing this browser session
    let id = localStorage.getItem('wastemap_anon_id');
    if (!id) {
      id = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      localStorage.setItem('wastemap_anon_id', id);
    }
    this.anonymousId = id;

    // Load any offline events backed up from prior sessions
    try {
      const stored = localStorage.getItem('wastemap_offline_analytics');
      if (stored) {
        this.offlineQueue = JSON.parse(stored);
        logger.info('analytics', `Restored ${this.offlineQueue.length} offline analytics events from localStorage.`);
      }
    } catch (e) {
      logger.error('analytics', 'Failed to recover offline analytics payload', e);
    }

    // Monitor network changes
    this.isOffline = !navigator.onLine;
    window.addEventListener('online', () => this.handleNetworkStatusChange(true));
    window.addEventListener('offline', () => this.handleNetworkStatusChange(false));

    // Start background flusher loop
    this.startPeriodicFlusher();
  }

  /**
   * Register third-party drivers (e.g. Firebase Analytics, Mixpanel, custom telemetry)
   */
  public registerDestination(destination: AnalyticsDestination) {
    try {
      destination.initialize();
      this.destinations.push(destination);
      if (this.currentUserId) {
        destination.setUserId?.(this.currentUserId);
      }
    } catch (e) {
      logger.error('analytics', `Failed to initialize destination: ${destination.name}`, e);
    }
  }

  /**
   * Set user identities safely upon signin.
   */
  public setUserId(userId: string | null) {
    this.currentUserId = userId;
    this.destinations.forEach((dest) => {
      try {
        dest.setUserId?.(userId);
      } catch (e) {
        logger.error('analytics', `Failed to set identity for destination: ${dest.name}`, e);
      }
    });
  }

  /**
   * Toggle LGPD privacy restrictions dynamically.
   * If true, scrub sensitive data descriptors before dispatching downstream tracking.
   */
  public setAnonymizeLogging(enable: boolean) {
    this.isReportingAnonymously = enable;
    logger.info('analytics', `LGPD Anonymization setting updated: ${enable ? 'ENABLED' : 'DISABLED'}`);
  }

  /**
   * Main dispatch route for strongly typed user interactions.
   */
  public track<TName extends string>({ name, props, category }: { name: TName; props: Record<string, any>; category: any }) {
    if (typeof window === 'undefined') return;

    // 1. Process LGPD-friendly scrubbing if user requested anonymity/obfuscation
    const treatedProps = this.isReportingAnonymously 
      ? this.anonymizeProps(props) 
      : { ...props };

    // 2. Build full canonical event object
    const event: AnalyticsEvent = {
      id: `ev_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      category,
      name,
      properties: treatedProps,
      anonymousId: this.anonymousId,
      userId: this.isReportingAnonymously ? null : this.currentUserId,
      userAgent: navigator.userAgent,
      screenResolution: `${window.innerWidth}x${window.innerHeight}`,
      isOnline: !this.isOffline,
      // Rule: created_at -> data_registro
      data_registro: new Date().toISOString(),
    };

    // 3. Queue logic based on connectivity state
    if (this.isOffline) {
      this.offlineQueue.push(event);
      this.syncOfflineQueueToStorage();
    } else {
      this.eventQueue.push(event);
      if (this.eventQueue.length >= this.batchSize) {
        this.flush();
      }
    }
  }

  /**
   * Standard batch delivery endpoint.
   */
  public async flush(): Promise<void> {
    if (this.isOffline || this.eventQueue.length === 0) return;

    const payloadToFlush = [...this.eventQueue];
    this.eventQueue = []; // Clear early to prevent concurrent race operations

    this.destinations.forEach((dest) => {
      try {
        if (dest.flush) {
          dest.flush(payloadToFlush);
        } else {
          payloadToFlush.forEach((ev) => dest.track(ev));
        }
      } catch (e) {
        logger.error('analytics', `Failed flushing package to destination: ${dest.name}`, e);
      }
    });

    logger.debug('analytics', `Successfully flushed ${payloadToFlush.length} events to registered providers.`);
  }

  /**
   * Recovers any offline telemetry when network state goes active.
   */
  private async flushOfflineQueue(): Promise<void> {
    if (this.isOffline || this.offlineQueue.length === 0) return;

    logger.info('analytics', `Network recovered. Flushing ${this.offlineQueue.length} queued offline interactions.`);
    
    const eventsToFlush = [...this.offlineQueue];
    this.offlineQueue = [];
    this.syncOfflineQueueToStorage();

    // Append directly to active queue and flush
    this.eventQueue.push(...eventsToFlush);
    await this.flush();
  }

  /**
   * Persistent safe store for offline tracking items.
   */
  private syncOfflineQueueToStorage() {
    try {
      localStorage.setItem('wastemap_offline_analytics', JSON.stringify(this.offlineQueue));
    } catch (e) {
      logger.error('analytics', 'Failed writing analytics offline queue to localstorage', e);
    }
  }

  /**
   * Scrubbing helper to ensure strict compliance with LGPD laws.
   */
  private anonymizeProps(props: Record<string, any>): Record<string, any> {
    const scrubbed = { ...props };
    const sensitiveKeys = ['email', 'password', 'nome', 'cpf', 'telefone', 'endereco', 'cpf_cnpj', 'avatar_url'];
    
    sensitiveKeys.forEach((key) => {
      if (key in scrubbed) {
        scrubbed[key] = '[ANONYMIZED_FOR_PRIVACY]';
      }
    });

    return scrubbed;
  }

  private handleNetworkStatusChange(isOnline: boolean) {
    this.isOffline = !isOnline;
    if (isOnline) {
      this.flushOfflineQueue();
    }
  }

  private startPeriodicFlusher() {
    if (this.timerId) clearInterval(this.timerId);
    this.timerId = setInterval(() => {
      this.flush();
    }, this.flushIntervalMs);
  }

  public shutdown() {
    if (this.timerId) clearInterval(this.timerId);
    window.removeEventListener('online', () => this.handleNetworkStatusChange(true));
    window.removeEventListener('offline', () => this.handleNetworkStatusChange(false));
  }
}

export const analyticsCore = new AnalyticsCore();

// Register a default verbose Console Logger Destination in Dev mode to easily observe active events
if (process.env.NODE_ENV !== 'production' || typeof window !== 'undefined') {
  analyticsCore.registerDestination({
    name: 'ConsoleTelemetryDriver',
    initialize: () => {
      logger.info('analytics', 'Console Telemetry Driver initialized successfully.');
    },
    track: (event) => {
      logger.debug('analytics', `[Event logged] ${event.name}`, event.properties);
    },
    flush: (events) => {
      logger.info('analytics', `[Batch Logged] Delivering packet of ${events.length} telemetry records.`, events);
    }
  });
}
